/**
 * Family Resource Mapper
 *
 * Maps FamilyMember[] to FamilyMemberResource[] and
 * CalendarEvent[] to FamilyResourceCalendarEvent[] for the family-based calendar view.
 *
 * Handles the event-to-member resolution algorithm:
 * 1. Add members from calendar-to-member mapping (always applies)
 * 2. Add members from description labels (always applies)
 * 3. If empty, default to "Me" member
 * 4. Duplicate events across columns for multi-member events
 */

import type { FamilyMember } from '@/types/family-member';
import type { CalendarMemberMapping } from '@/types/calendar-member-mapping';
import type { CalendarEvent } from '@/types/events';
import type {
  FamilyMemberResource,
  FamilyResourceCalendarEvent,
  AllDayEventsByFamilyMember,
} from '@/types/family-resource';
import { EventDescriptionSectionParser } from '@/lib/utils/EventDescriptionSectionParser';
import { TaskCountResolver } from '@/lib/utils/TaskCountResolver';

export class FamilyResourceMapper {
  /**
   * Map FamilyMember array to FamilyMemberResource array
   */
  static mapMembersToResources(
    members: FamilyMember[],
    meDisplayName?: string,
  ): FamilyMemberResource[] {
    return members.map(member => ({
      id: member.id,
      name: (member.isMe && meDisplayName) ? meDisplayName : member.name,
      color: member.color,
      isMe: member.isMe,
      profilePicture: member.profilePicture,
    }));
  }

  /**
   * Resolve which family members an event belongs to
   *
   * Resolution order:
   * 1. Calendar-to-member mappings (always applies)
   * 2. Description labels (always applies)
   * 3. Default to "Me" member if none found
   */
  static resolveEventMembers(
    event: CalendarEvent,
    calendarMappings: CalendarMemberMapping[],
    meMemberId: string
  ): string[] {
    const memberIds = new Set<string>();

    // 1. Add members from calendar mapping (always applies)
    const calendarMapping = calendarMappings.find(
      m => m.calendarId === event.deviceCalendarId
    );
    if (calendarMapping) {
      calendarMapping.memberIds.forEach(id => memberIds.add(id));
    }

    // Check if Me is toggled off via metoggle section — remove from set
    const meToggleOff = EventDescriptionSectionParser.extractMeToggleOff(event.description);
    if (meToggleOff) {
      memberIds.delete(meMemberId);
    }

    // 2. Add from description labels (always applies)
    const descriptionMemberIds = EventDescriptionSectionParser.extractFamilyMemberIds(
      event.description
    );
    descriptionMemberIds.forEach(id => memberIds.add(id));

    // 3. Default to "Me" only for legacy events (no explicit ---family--- section).
    // New events with an empty family section intentionally have no labels.
    const hasFamilySection = EventDescriptionSectionParser.hasFamilySection(event.description);
    if (memberIds.size === 0 && !hasFamilySection) {
      memberIds.add(meMemberId);
    }

    return Array.from(memberIds);
  }

  /**
   * Map CalendarEvent to FamilyResourceCalendarEvent for a specific member
   */
  static mapEventToFamilyResourceEvent(
    event: CalendarEvent,
    memberId: string,
    memberColor: string
  ): FamilyResourceCalendarEvent {
    const startDateTime = this.buildISODateTime(event.startDate, event.startTime);
    const endDateTime = this.buildISODateTime(
      event.endDate || event.startDate,
      event.endTime || event.startTime
    );

    const taskCount = TaskCountResolver.resolve(event.description);

    return {
      id: `${event.id}_${memberId}`, // Unique ID per member column
      originalEventId: event.id,
      title: event.title,
      start: { dateTime: startDateTime },
      end: { dateTime: endDateTime },
      color: memberColor,
      resourceId: memberId,
      location: event.location || undefined,
      deviceCalendarId: event.deviceCalendarId,
      openTaskCount: taskCount?.openCount,
      totalTaskCount: taskCount?.totalCount,
    };
  }

  /**
   * Map array of CalendarEvents to FamilyResourceCalendarEvents
   * Events appear in multiple columns if they belong to multiple members
   */
  static mapEventsToFamilyResourceEvents(
    events: CalendarEvent[],
    members: FamilyMember[],
    calendarMappings: CalendarMemberMapping[],
    meMemberId: string
  ): FamilyResourceCalendarEvent[] {
    const memberColorMap = new Map(members.map(m => [m.id, m.color]));
    const validMemberIds = new Set(members.map(m => m.id));
    const result: FamilyResourceCalendarEvent[] = [];

    for (const event of events) {
      const memberIds = this.resolveEventMembers(event, calendarMappings, meMemberId);

      // Create an event instance for each member column
      for (const memberId of memberIds) {
        // Only include if member still exists
        if (!validMemberIds.has(memberId)) continue;

        const color = memberColorMap.get(memberId) || '#4CAF50';
        result.push(this.mapEventToFamilyResourceEvent(event, memberId, color));
      }
    }

    return result;
  }

  /**
   * Filter events for a specific date
   */
  static filterEventsForDate(
    events: CalendarEvent[],
    targetDate: string // YYYY-MM-DD
  ): CalendarEvent[] {
    return events.filter(event => {
      // Include if event starts on target date
      if (event.startDate === targetDate) return true;

      // Include if event spans target date (exclusive end date)
      if (event.endDate && event.startDate < targetDate && event.endDate > targetDate) {
        return true;
      }

      return false;
    });
  }

  /**
   * Separate all-day events from timed events
   */
  static separateAllDayEvents(events: CalendarEvent[]): {
    allDay: CalendarEvent[];
    timed: CalendarEvent[];
  } {
    const allDay: CalendarEvent[] = [];
    const timed: CalendarEvent[] = [];

    events.forEach(event => {
      if (event.allDay || !event.startTime) {
        allDay.push(event);
      } else {
        timed.push(event);
      }
    });

    return { allDay, timed };
  }

  /**
   * Group all-day events by family member
   */
  static groupAllDayEventsByMember(
    allDayEvents: CalendarEvent[],
    members: FamilyMember[],
    calendarMappings: CalendarMemberMapping[],
    meMemberId: string
  ): AllDayEventsByFamilyMember {
    const map = new Map<string, CalendarEvent[]>();
    const validMemberIds = new Set(members.map(m => m.id));

    // Initialize map with empty arrays for all members
    members.forEach(member => {
      map.set(member.id, []);
    });

    for (const event of allDayEvents) {
      const memberIds = this.resolveEventMembers(event, calendarMappings, meMemberId);

      for (const memberId of memberIds) {
        if (!validMemberIds.has(memberId)) continue;

        const existing = map.get(memberId) || [];
        map.set(memberId, [...existing, event]);
      }
    }

    return map;
  }

  /**
   * Build ISO datetime string from date and optional time
   */
  private static buildISODateTime(dateStr: string, timeStr?: string | null): string {
    if (!timeStr) {
      return `${dateStr}T00:00:00`;
    }
    return `${dateStr}T${timeStr}:00`;
  }
}
